import {
  ICombinedSimpleModel,
  ISimpleFilterModel,
} from '@ag-grid-community/core'
import { ScalerFilterModel } from '@northfork/ag-grid-angular'
import { FilterType, MultiFilter, SingleFilter } from './filter'

export interface DateFilter extends MultiFilter<string> {
  filterType: FilterType.DateTime | FilterType.Date
  filters: SingleDateFilter[]
}

export type DateFilterOperator =
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'blank'
  | 'notBlank'

interface SingleDateFilter extends SingleFilter<string> {
  operator: DateFilterOperator
  value?: string
  // populated if the operator is 'inRange'
  range?: Range
}

interface Range {
  from: string
  to: string
}

/**
 * Check if the date filter operator is in the defined list
 * defined here: https://stash.arbfund.com/projects/HEL/repos/helios/browse/libs/request-model/src/main/kotlin/com/clearwateranalytics/helios/requests/filter/DateFilter.kt#49-57
 */
function isDateFilterOperator(s: string): s is DateFilterOperator {
  return [
    'equals',
    'notEqual',
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ].includes(s)
}

/**
 * Convert combined into a date filter
 */
export function convertICombinedSimpleModelToDateFilter(
  combinedFilter: ICombinedSimpleModel<ISimpleFilterModel>,
): DateFilter | null {
  if (
    combinedFilter.condition1.filterType !== 'date' ||
    combinedFilter.condition2.filterType !== 'date'
  ) {
    console.error('Filter type should be date', combinedFilter)
    return null
  }
  const firstFilterModel =
    combinedFilter.condition1 as ScalerFilterModel<string>
  const secondFilterModel =
    combinedFilter.condition2 as ScalerFilterModel<string>

  // The combined shape hardcodes FilterType.Date below, but a value-less
  // condition needs FilterType.DateTime to hit the right backend subtype (see
  // detectIfDateTime). Refuse rather than emit a request the backend rejects.
  // A blank that carries a stand-in value still converts, as it did before —
  // that is the shape filter-editor-utils and the Clearwater toggle produce.
  if (
    isValuelessCondition(firstFilterModel) ||
    isValuelessCondition(secondFilterModel)
  ) {
    console.error(
      'blank/notBlank without a value is not supported combined',
      combinedFilter,
    )
    return null
  }

  const firstSingleFilter = convertSingleFilter(firstFilterModel)
  const secondSingleFilter = convertSingleFilter(secondFilterModel)

  if (firstSingleFilter && secondSingleFilter) {
    return {
      operator: combinedFilter.operator,
      filterType: FilterType.Date,
      filters: [firstSingleFilter, secondSingleFilter],
    }
  } else {
    return null
  }
}

/**
 * Convert simple filter to a date filter
 */
export function convertISimpleFilterModelToDateFilter(
  filter: ISimpleFilterModel,
): DateFilter | null {
  if (
    filter.filterType !== FilterType.Date &&
    filter.filterType !== FilterType.DateTime
  ) {
    console.error('Filter type should be date', filter)
    return null
  }

  const filterModel = filter as ScalerFilterModel<string> //Not sure why this is not DateFilterModel, but I have tested it ag-grid always sends ScalerFilterModel
  const singleFilter = convertSingleFilter(filterModel)
  if (singleFilter) {
    const shouldBeDateTime = detectIfDateTime(filterModel)
    return {
      operator: 'AND',
      filterType: shouldBeDateTime ? FilterType.DateTime : FilterType.Date,
      filters: [singleFilter],
    }
  } else {
    return null
  }
}

/**
 * Convert scalar filter to a simple filter
 */
function convertSingleFilter(filterModel: ScalerFilterModel<string>) {
  if (filterModel.type && isDateFilterOperator(filterModel.type)) {
    const singleDateFilter: SingleDateFilter = {
      operator: filterModel.type,
    }
    if (filterModel.type === 'inRange') {
      if (filterModel.filter && filterModel.filterTo) {
        singleDateFilter.range = {
          from: detectAndConvertDateTime(filterModel.filter),
          to: detectAndConvertDateTime(filterModel.filterTo),
        }
      } else {
        console.error('inRange requires filter and filterTo', filterModel)
        return null
      }
    } else {
      // Northfork's date components default `filter` to '' rather than null, so
      // without the operator guard blank/notBlank land in the missing-value
      // branch and convertFilters drops them — the filter silently does nothing.
      if (filterModel.filter) {
        singleDateFilter.value = detectAndConvertDateTime(filterModel.filter)
      } else if (!isValuelessDateOperator(filterModel.type)) {
        console.error('Missing filter', filterModel)
        return null
      }
    }
    return singleDateFilter
  } else {
    console.error('Filter operator not supported', filterModel)
    return null
  }
}

// Regex to detect if time is included
const IS_DATE_TIME = /[0-9]+-[0-9]+-[0-9]+T.*/

/**
 * Use regex to determine if time is included
 */
function isDateTime(value: string) {
  return value?.match(IS_DATE_TIME) != null
}

/** blank/notBlank are the only date operators that carry no value. */
function isValuelessDateOperator(type?: string) {
  return type === 'blank' || type === 'notBlank'
}

/** A blank/notBlank condition with no value to fall back on. */
function isValuelessCondition(filterModel: ScalerFilterModel<string>) {
  return isValuelessDateOperator(filterModel.type) && !filterModel.filter
}

/**
 * Check if the filter is a date time
 */
function detectIfDateTime(filter: ScalerFilterModel<string>) {
  if (filter.filter) {
    return isDateTime(filter.filter)
  }
  // With no value there is nothing to sniff, and filterType selects the backend
  // filter subtype (@JsonTypeInfo on Filter) — answering 'date' for a field
  // declared DateTimeFilter fails deserialization. Only the DateTime column
  // types offer blank/notBlank (see the filterOptions above), so a value-less
  // date filter is always a datetime one.
  return isValuelessDateOperator(filter.type)
}

/**
 * Detect and convert to date time format
 */
function detectAndConvertDateTime(value: string): string {
  if (isDateTime(value)) {
    return new Date(value).toISOString()
  }

  return value
}
